import type { ReactNode } from 'react';
import {
    Box,
    MenuItem,
    Select,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { BottomSheet } from '../../../../components/touch/BottomSheet';
import { RESOLUTION_OPTIONS } from '../../components/charts/convergence/constants';
import { segmentedSx } from './segmented';

// What the phone toolbar keeps out of its one row: the view (stacked, line,
// candles), the resolution, the volume and the download. The download is
// whatever the page passes (the event page hands in its ExportBox, so the
// export dialog is the same one the desktop toolbar opens).

const AUTO = 'auto';

export interface ViewOption<T extends string> {
    value: T;
    label: string;
    disabled?: boolean;
}

export interface ChartOptionsSheetProps<T extends string> {
    open: boolean;
    onClose: () => void;
    view: {
        value: T;
        options: readonly ViewOption<T>[];
        onChange: (value: T) => void;
        /** Why a view is greyed out: a tooltip cannot say it on a phone. */
        hint?: string;
    };
    resolution: {
        choice: number | null;
        label: string;
        onChange: (choice: number | null) => void;
    };
    volume?: { shown: boolean; onToggle: () => void };
    download?: ReactNode;
}

function Row({
    label,
    children,
    component = 'div',
}: {
    label: string;
    children: ReactNode;
    component?: 'div' | 'label';
}) {
    return (
        <Box
            component={component}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                minHeight: 52,
                px: 2.5,
                borderTop: 1,
                borderColor: 'divider',
            }}
        >
            <Typography component="span" sx={{ fontSize: '16px' }}>
                {label}
            </Typography>
            {children}
        </Box>
    );
}

export function ChartOptionsSheet<T extends string>({
    open,
    onClose,
    view,
    resolution,
    volume,
    download,
}: ChartOptionsSheetProps<T>) {
    return (
        <BottomSheet
            open={open}
            onClose={onClose}
            title="Chart options"
            handleGap={12}
        >
            <Box sx={{ px: 2.5, pb: 1.5 }}>
                <Typography
                    sx={{ fontSize: '13px', color: 'text.secondary', py: 1 }}
                >
                    View
                </Typography>
                <ToggleButtonGroup
                    exclusive
                    fullWidth
                    value={view.value}
                    aria-label="Chart view"
                    onChange={(_, next: T | null) => {
                        if (next) view.onChange(next);
                    }}
                    sx={[
                        segmentedSx(8),
                        {
                            '& .MuiToggleButton-root': {
                                height: 44,
                                fontSize: '15px',
                                fontWeight: 400,
                                textTransform: 'none',
                                color: (theme: Theme) =>
                                    alpha(theme.palette.text.primary, 0.8),
                                '&.Mui-selected, &.Mui-selected:hover': {
                                    fontWeight: 500,
                                    color: 'primary.main',
                                    bgcolor: (theme: Theme) =>
                                        alpha(theme.palette.primary.main, 0.12),
                                },
                                '&.Mui-disabled': {
                                    color: (theme: Theme) =>
                                        alpha(theme.palette.text.primary, 0.38),
                                },
                            },
                        },
                    ]}
                >
                    {view.options.map(option => (
                        <ToggleButton
                            key={option.value}
                            value={option.value}
                            disabled={option.disabled}
                        >
                            {option.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                {view.hint && (
                    <Typography
                        sx={{
                            fontSize: '12px',
                            color: 'text.secondary',
                            mt: 1,
                        }}
                    >
                        {view.hint}
                    </Typography>
                )}
            </Box>

            <Row label="Resolution">
                <Select
                    value={resolution.choice ?? AUTO}
                    size="small"
                    variant="standard"
                    disableUnderline
                    onChange={event => {
                        const value = event.target.value;
                        resolution.onChange(
                            value === AUTO ? null : Number(value)
                        );
                    }}
                    renderValue={value =>
                        value === AUTO
                            ? `Auto (${resolution.label})`
                            : resolution.label
                    }
                    inputProps={{ 'aria-label': 'Resolution' }}
                    sx={{
                        fontSize: '16px',
                        color: 'primary.main',
                        '& .MuiSelect-select': {
                            minHeight: 44,
                            display: 'flex',
                            alignItems: 'center',
                            pl: 1.5,
                        },
                        '& .MuiSelect-icon': { color: 'primary.main' },
                    }}
                >
                    <MenuItem
                        value={AUTO}
                        sx={{ minHeight: 48, fontSize: '16px' }}
                    >
                        Auto
                    </MenuItem>
                    {RESOLUTION_OPTIONS.map(option => (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                            sx={{ minHeight: 48, fontSize: '16px' }}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </Row>

            {volume && (
                <Row label="Show volume" component="label">
                    <Switch checked={volume.shown} onChange={volume.onToggle} />
                </Row>
            )}

            {download && <Row label="Download data">{download}</Row>}
        </BottomSheet>
    );
}
